import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../shared/components/Icon';
import { DockMenu, type DockMenuItem } from './DockMenu';
import { useDockDrag } from './dockDrag';
import type { Rect } from './dockModel';
import { describeAddress } from './panelCatalog';
import { useDockElements, useWorkspace, useWorkspaceActions } from './WorkspaceProvider';
import { panelHostId } from './workspaceState';

/** Each new window steps down and across from the last, the way a stack of papers does. */
function cascadeRect(canvas: HTMLElement | null, taken: number): Rect {
  const bounds = canvas?.getBoundingClientRect();
  const canvasWidth = bounds?.width ?? 1200;
  const canvasHeight = bounds?.height ?? 800;
  const width = Math.min(820, Math.max(canvasWidth * 0.56, 320));
  const height = Math.min(560, Math.max(canvasHeight * 0.62, 220));
  const step = (taken % 6) * 28;
  return {
    x: Math.max(0, Math.min(canvasWidth * 0.18 + step, canvasWidth - width)),
    y: Math.max(0, Math.min(canvasHeight * 0.12 + step, canvasHeight - height)),
    width,
    height,
  };
}

/**
 * Every command that acts on one document, in one place, so the tab's context menu and
 * the pane's overflow menu can never drift apart.
 */
export function useDocumentCommands(): (panelId: string) => DockMenuItem[] {
  const { t } = useTranslation();
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const { canvas } = useDockElements();

  return useCallback((panelId: string) => {
    const panel = state.panels[panelId];
    if (panel === undefined) return [];
    const hostId = panelHostId(state, panelId);
    const inWindow = state.windows.some((window) => window.id === hostId);

    const items: DockMenuItem[] = [
      { id: 'close', label: t('workspace.closeDocument'), icon: 'close', hint: 'Ctrl W', run: () => dispatch({ type: 'close', panelId }) },
      { id: 'close-others', label: t('workspace.closeOthers'), run: () => dispatch({ type: 'closeOthers', panelId }) },
      { id: 'close-all', label: t('workspace.closeAll'), run: () => dispatch({ type: 'closeAll' }) },
      {
        id: 'pin',
        label: panel.pinned ? t('workspace.unpin') : t('workspace.pin'),
        icon: 'lock',
        separated: true,
        run: () => dispatch({ type: 'togglePin', panelId }),
      },
      {
        id: 'duplicate',
        label: t('workspace.duplicate'),
        icon: 'copy',
        run: () => dispatch({ type: 'open', address: panel.address, mode: 'new' }),
      },
    ];

    if (hostId !== null && !inWindow) {
      items.push(
        {
          id: 'split-beside',
          label: t('workspace.splitBeside'),
          icon: 'splitBeside',
          separated: true,
          run: () => dispatch({ type: 'movePanel', panelId, target: { kind: 'group', groupId: hostId, zone: 'end' } }),
        },
        {
          id: 'split-below',
          label: t('workspace.splitBelow'),
          icon: 'splitBelow',
          run: () => dispatch({ type: 'movePanel', panelId, target: { kind: 'group', groupId: hostId, zone: 'bottom' } }),
        },
      );
    }

    items.push(
      inWindow
        ? {
          id: 'dock',
          label: t('workspace.dockDocument'),
          icon: 'dock',
          separated: true,
          run: () => dispatch({ type: 'movePanel', panelId, target: { kind: 'group', groupId: state.activeGroupId, zone: 'center' } }),
        }
        : {
          id: 'float',
          label: t('workspace.floatDocument'),
          icon: 'float',
          separated: true,
          run: () => dispatch({ type: 'movePanel', panelId, target: { kind: 'float', rect: cascadeRect(canvas, state.windows.length) } }),
        },
    );

    return items;
  }, [canvas, dispatch, state, t]);
}

interface DockTabsProps {
  readonly hostId: string;
  readonly panelIds: readonly string[];
  readonly activePanelId: string | null;
  readonly focused: boolean;
}

export function DockTabs({ hostId, panelIds, activePanelId, focused }: DockTabsProps) {
  const { t } = useTranslation();
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const { registerTabStrip } = useDockElements();
  const { startPanelDrag } = useDockDrag();
  const commandsFor = useDocumentCommands();
  const [menu, setMenu] = useState<{ panelId: string; x: number; y: number } | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);

  const attachStrip = useCallback((element: HTMLDivElement | null) => {
    strip.current = element;
    registerTabStrip(hostId, element);
    return () => registerTabStrip(hostId, null);
  }, [hostId, registerTabStrip]);

  // A tab brought forward by a keyboard shortcut or by the command palette has to be
  // scrolled to as well, or the workspace looks like it ignored the request.
  useEffect(() => {
    if (activePanelId === null) return;
    strip.current?.querySelector<HTMLElement>(`[data-panel-id="${CSS.escape(activePanelId)}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activePanelId]);

  const tabs = useMemo(
    () => panelIds.flatMap((panelId) => {
      const panel = state.panels[panelId];
      return panel === undefined ? [] : [{ panel, identity: describeAddress(panel.address, t) }];
    }),
    [panelIds, state.panels, t],
  );

  const moveFocus = (currentIndex: number, step: number) => {
    const next = tabs[(currentIndex + step + tabs.length) % Math.max(tabs.length, 1)];
    if (next === undefined) return;
    dispatch({ type: 'activate', panelId: next.panel.id });
    strip.current?.querySelector<HTMLElement>(`[data-panel-id="${CSS.escape(next.panel.id)}"]`)?.focus();
  };

  return (
    <div
      className={`dock-tabs${focused ? ' dock-tabs--focused' : ''}`}
      role="tablist"
      aria-label={t('workspace.openDocuments')}
      ref={attachStrip}
    >
      {tabs.map(({ panel, identity }, index) => {
        const isActive = panel.id === activePanelId;
        return (
          <div
            key={panel.id}
            data-dock-tab=""
            data-panel-id={panel.id}
            role="tab"
            tabIndex={isActive ? 0 : -1}
            aria-selected={isActive}
            aria-controls={`dock-document-${panel.id}`}
            title={`${identity.title}${identity.detail === null ? '' : ` · ${identity.detail}`}`}
            className={`dock-tab${isActive ? ' dock-tab--active' : ''}${panel.pinned ? ' dock-tab--pinned' : ''}`}
            onPointerDown={(event) => {
              if (event.button === 1) return;
              dispatch({ type: 'activate', panelId: panel.id });
              startPanelDrag(event, { id: panel.id, label: identity.title, icon: identity.icon });
            }}
            onAuxClick={(event) => {
              if (event.button !== 1) return;
              event.preventDefault();
              dispatch({ type: 'close', panelId: panel.id });
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              setMenu({ panelId: panel.id, x: event.clientX, y: event.clientY });
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') { event.preventDefault(); moveFocus(index, document.documentElement.dir === 'rtl' ? -1 : 1); }
              if (event.key === 'ArrowLeft') { event.preventDefault(); moveFocus(index, document.documentElement.dir === 'rtl' ? 1 : -1); }
              if (event.key === 'Delete') { event.preventDefault(); dispatch({ type: 'close', panelId: panel.id }); }
              if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); dispatch({ type: 'activate', panelId: panel.id }); }
            }}
          >
            <Icon name={identity.icon} className="icon icon--small dock-tab__icon" />
            <span className="dock-tab__label">{identity.title}</span>
            {identity.detail === null ? null : <em className="dock-tab__detail">{identity.detail}</em>}
            {panel.pinned ? <Icon name="lock" className="icon icon--small dock-tab__pin" /> : null}
            <button
              type="button"
              className="dock-tab__close"
              aria-label={t('workspace.closeNamedDocument', { name: identity.title })}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                dispatch({ type: 'close', panelId: panel.id });
              }}
            >
              <Icon name="close" className="icon icon--small" />
            </button>
          </div>
        );
      })}

      {menu === null ? null : (
        <DockMenu
          x={menu.x}
          y={menu.y}
          label={t('workspace.documentMenu')}
          items={commandsFor(menu.panelId)}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
